/**
 * Decides whether the Dynamic Table prompt should open for a guest arriving
 * through a `/dynamic-table/:slug` link.
 *
 * Extracted from the menu effect because the two ways this silently breaks are
 * both invisible at runtime: reading the flag off the wrong entity, and
 * treating "not fetched yet" as "disabled". Both fail closed — the guest just
 * sees a normal menu with no prompt, and nothing is logged as an error.
 */

// A loaded order config is an object carrying `isEnabled`. An unloaded one is
// undefined, null, or a bare ID string.
export const isConfigLoaded = (config) =>
  Boolean(config && typeof config === 'object' && 'isEnabled' in config);

const ORDER_CONFIG_KEYS = [
  'pickupOrderConfig',
  'selfServiceOrderConfig',
  'inhouseOrderConfig',
  'deliveryOrderConfig',
  'cateringOrderConfig',
];

export const hasLoadedOrderConfigs = (store) =>
  ORDER_CONFIG_KEYS.some((key) => isConfigLoaded(store?.[key]));

/**
 * @returns {'idle'|'waiting'|'enabled'|'disabled'}
 *   - `idle`: not a dynamic-table link, no store, or admin mode — do nothing
 *   - `waiting`: configs not fetched yet, re-run when they arrive
 *   - `enabled`: open the prompt
 *   - `disabled`: the store switched the feature off
 */
export const resolveDynamicTableState = ({ pathname, store, isAdminMode }) => {
  if (!pathname?.startsWith('/dynamic-table/') || !store || isAdminMode) {
    return 'idle';
  }

  // The flag lives on the Inhouse order config. `store.merchantProfile` is
  // loaded via `loadRelationIds`, so on public endpoints it is a UUID string
  // and reading a boolean off it is always undefined — which reads as
  // "disabled". Do not read the flag from there.
  if (!isConfigLoaded(store.inhouseOrderConfig)) {
    return 'waiting';
  }

  return store.inhouseOrderConfig.enableDynamicTableLink
    ? 'enabled'
    : 'disabled';
};
